import React from 'react'
import { t } from 'ttag'

import { ActionsDropdown } from 'components/shared/ActionsDropdown/ActionsDropdown'
import { ActionsCell } from 'components/shared/Table/common/Cells/ActionsCell/ActionsCell'
import { HeaderCell } from 'components/shared/Table/common/Cells/HeaderCell/HeaderCell'
import { Pagination } from 'components/shared/Table/common/Pagination/Pagination'
import { Table } from 'components/shared/Table/Table'
import { FileTextIcon } from 'components/shared/Icon/model'
import { IconButton } from 'components/shared/IconButton/IconButton'
import { JobSeekerResultsModal } from 'components/modals/JobSeekerResultsModal/JobSeekerResultsModal'
import { useResultsTable } from './hooks'
import { getActions } from './helpers'

import styles from './ResultsTable.module.scss'
import { CandidateCell } from 'components/shared/Table/common/Cells/CandidateCell/CandidateCell'
import { JobProgressCell } from 'components/shared/Table/common/Cells/JobProgressCell/JobProgressCell'
import { DateCell } from 'components/shared/Table/common/Cells/DateCell/DateCell'

export const ResultsTable = ({
  search,
  handleCopy,
  handleDelete,
  handleEdit,
}) => {
  const { state, handlers } = useResultsTable({ search })

  const {
    pagination: { currentPage, pageCount, perPage },
    table: { headers, model, rowActions, isFetching },
    isOpen,
    jobSeeker,
  } = state
  const {
    pagination: { handlePage, handlePageSize },
    handleOpenModal,
    handleClose,
  } = handlers

  return (
    <>
      <Table>
        <Table.Head>
          <Table.Row type="head">
            {headers.map(
              ({ columnDef, getToggleSortingHandler, getIsSorted }) => {
                const { accessorKey, header, alignLeft } = columnDef

                return (
                  <HeaderCell
                    key={header}
                    alignLeft={!!alignLeft}
                    sortType={getIsSorted()}
                    text={header}
                    onClick={getToggleSortingHandler()}
                    hasSorting={accessorKey}
                  />
                )
              },
            )}
          </Table.Row>
        </Table.Head>
        <Table.Body
          placeholder={{
            title: t`No results yet`,
            text: t`Try adjusting your search`,
          }}
          isLoaded={!isFetching}
        >
          {model.rows.map(({ id, original }) => {
            if (!original?.timestamps?.created) {
              return null
            }

            const actions = getActions(
              { actions: rowActions, jobSeeker: original },
              { handleCopy, handleDelete, handleEdit },
            ).filter(action => action.type !== 'ACTION_COPY')

            const { email, name, selection_stage, status, timestamps } =
              original

            const isCompleted = status === 'completed'

            return (
              <Table.Row key={id} type="body">
                <CandidateCell email={email} name={name} indentIcon />
                <DateCell date={timestamps?.[status]} hasNowLabel />
                <JobProgressCell
                  progress={selection_stage - 1}
                  hasInterviewModule={false}
                  status={status}
                  type="candidate"
                />
                <ActionsCell className={styles.actionsCell}>
                  <IconButton
                    className={styles.button}
                    icon={FileTextIcon}
                    size="medium"
                    message={
                      isCompleted
                        ? null
                        : t`Results available after finishing both tests`
                    }
                    variant="text"
                    isDisabled={!isCompleted}
                    onClick={() => handleOpenModal(original)}
                  />
                  <ActionsDropdown
                    anchorClassName={styles.button}
                    size="medium"
                    {...{ actions }}
                  />
                </ActionsCell>
              </Table.Row>
            )
          })}
        </Table.Body>
      </Table>
      {isOpen ? (
        <JobSeekerResultsModal
          onCancel={handleClose}
          onClose={handleClose}
          jobSeeker={jobSeeker}
          isOpen={true}
        />
      ) : null}
      <Pagination
        onSetPage={handlePage}
        onSetPageSize={handlePageSize}
        {...{ currentPage, pageCount, perPage }}
      />
    </>
  )
}
